<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BottomSheet, { type BottomSheetPosition } from '@/components/layout/BottomSheet.vue';
import ActionButton from '@/components/ui/ActionButton.vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import Common from '@/libs/common';
import type { JourneyLeg, TrackedJourney } from '@/libs/journey/journeys';
import type { Bounds } from '@/stores/gtfs';
import { MapFocusType, useAppStore } from '@/stores/app';
import JourneyTimeline from './JourneyTimeline.vue';

/**
 * The journey being followed: time left, estimated arrival and distance, kept up to
 * date with the live data, and a warning when a connection can no longer be made.
 */
const props = defineProps<{
  tracked: TrackedJourney;
  /** Name of the start, where the first walk sets off when the journey starts at an address. */
  originLabel: string;
  /** Name of the arrival, the end of the last walk when the journey ends at an address. */
  destinationLabel?: string;
}>();

const emit = defineEmits<{ back: []; end: []; recalculate: [] }>();

const { t, d, locale } = useI18n();
const appStore = useAppStore();

const sheetPosition = ref<BottomSheetPosition>('collapsed');

const now = ref<number>(Date.now() / 1000);
let clock: number | undefined;

const journey = computed(() => props.tracked.journey);

const remaining = computed<string>(() => Common.formatSec(journey.value.arrival - now.value));

const distance = computed<string>(() =>
  new Intl.NumberFormat(locale.value, { style: 'unit', unit: 'kilometer', maximumFractionDigits: 1 }).format(
    journey.value.distance / 1000,
  ),
);

const summary = computed<string>(() =>
  t('itinerary.tracking.arrival', {
    time: d(journey.value.arrival * 1000, 'timeShort'),
    distance: distance.value,
  }),
);

// Changes between quays of the same stop take no walk: they are no step of their own.
const legs = computed<JourneyLeg[]>(() =>
  journey.value.legs.filter(leg => leg.type === 'transit' || leg.duration > 0),
);

function frameJourney() {
  const bounds: Bounds = { north: null, south: null, east: null, west: null };
  journey.value.legs.forEach(leg => {
    const points =
      leg.type === 'transit'
        ? leg.path
        : [
            [leg.from.lon, leg.from.lat],
            [leg.to.lon, leg.to.lat],
          ];
    points.forEach(([lon, lat]) => {
      if (bounds.north === null || lat > bounds.north) bounds.north = lat;
      if (bounds.south === null || lat < bounds.south) bounds.south = lat;
      if (bounds.east === null || lon > bounds.east) bounds.east = lon;
      if (bounds.west === null || lon < bounds.west) bounds.west = lon;
    });
  });
  if (bounds.north !== null) appStore.setMapFocus({ type: MapFocusType.BOUNDS, elem: bounds });
}

function onSheetHeight(height: number, dragging: boolean) {
  appStore.setMapBottomInset(height, dragging);
}

let unregisterBackHandler: (() => void) | undefined;

onMounted(() => {
  frameJourney();
  // Asked once as the tracking starts: refused, the timeline goes by the times alone.
  appStore.getGeolocation({ focusMap: false });
  // The position comes along with the clock: it places the traveller on the timeline.
  clock = window.setInterval(() => {
    now.value = Date.now() / 1000;
    appStore.getGeolocationIfAllowed();
  }, 15_000);
  unregisterBackHandler = appStore.registerBackHandler(() => {
    emit('back');
    return true;
  });
});

onBeforeUnmount(() => {
  window.clearInterval(clock);
  unregisterBackHandler?.();
});
</script>

<template>
  <div class="journey-tracking">
    <div class="journey-tracking__top">
      <RoundedButton
        :aria-label="$t('common.back')"
        class="journey-tracking__back"
        icon="arrow-left"
        size="large"
        type="outline"
        @click="emit('back')"
      />
    </div>

    <div class="journey-tracking__sheet-area">
      <BottomSheet v-model:position="sheetPosition" @update:height="onSheetHeight">
        <template #header>
          <div class="journey-tracking__header">
            <div class="journey-tracking__summary">
              <span class="journey-tracking__remaining">{{ remaining }}</span>
              <span class="journey-tracking__arrival">{{ summary }}</span>
            </div>
            <ActionButton
              :label="$t('itinerary.tracking.end')"
              size="large"
              type="primary"
              @click="emit('end')"
            />
          </div>

          <div v-if="tracked.issue" class="journey-tracking__issue" role="alert">
            <BaseIcon name="triangle-alert" class="journey-tracking__issue-icon" />
            <div class="journey-tracking__issue-text">
              <strong>{{ $t('itinerary.tracking.issue.title') }}</strong>
              <span>{{ $t(`itinerary.tracking.issue.${tracked.issue}`) }}</span>
            </div>
            <ActionButton
              :label="$t('itinerary.tracking.recalculate')"
              type="primary"
              @click="emit('recalculate')"
            />
          </div>
        </template>

        <JourneyTimeline
          :destination-label="destinationLabel"
          :legs="legs"
          :now="now"
          :origin-label="originLabel"
          :position="appStore.positionGeolocation"
        />
      </BottomSheet>
    </div>
  </div>
</template>

<style lang="scss">
.journey-tracking {
  inset: 0;
  pointer-events: none;
  position: absolute;

  &__top {
    left: 0;
    padding: calc(4px + var(--status-bar-band)) $spacing-4 0;
    position: absolute;
    top: 0;
    z-index: 3;
  }

  &__back {
    box-shadow: $shadow-soft;
    pointer-events: auto;
  }

  &__sheet-area {
    bottom: 0;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: calc(64px + var(--status-bar-band));
  }

  &__header {
    align-items: flex-start;
    border-bottom: 1px solid $color-border-light;
    display: flex;
    gap: $spacing-5;
    padding: $spacing-4;
  }

  &__summary {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-1;
    min-width: 0;
  }

  &__remaining {
    color: $color-neutral-highest;
    // Figma Heading/H2: the design system has no heading token this size yet.
    font: 600 24px / 32px $font-poppins;
    letter-spacing: $letter-spacing-tighter;
  }

  &__arrival {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
  }

  &__issue {
    align-items: center;
    background-color: $color-warning-light;
    border-radius: $radius-card;
    color: $color-warning-dark;
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-3;
    margin: $spacing-3 $spacing-4;
    padding: $spacing-3;
  }

  &__issue-icon {
    flex-shrink: 0;
    font-size: 20px;
  }

  &__issue-text {
    display: flex;
    flex: 1;
    flex-direction: column;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    min-width: 160px;

    strong {
      font: $label-small;
    }
  }
}
</style>
